import React, { ReactElement, useEffect } from "react"
import { Card, Typography, Box } from "@mui/material"
import { MainPageContentContainer } from "components/MainPageContentContainer.tsx"
import { OreveButton } from "components/button/OreveButton.tsx"
import { useMmsService } from "services/mms/context/MmsServiceContext.tsx"
import { EntityInfo } from "types/entity"
import { InvoiceInfo } from "types/list-invoice"
import { StatusError as Error, StatusValidation as Validation, StatusWaiting as Waiting } from "components/status"
import { SessionInfo } from "types/sessions"
import {useNavigate} from "react-router-dom";
import ReceiptIcon from '@mui/icons-material/Receipt';
import EuroIcon from '@mui/icons-material/Euro';
import Thunder from 'assets/svg/thunder.svg';
import Money from 'assets/svg/Money.svg'

export const DashboardInvoice = () => {
    const navigate = useNavigate()
    const { sessionsService, invoiceService, profileService} = useMmsService()
    const { useGetMonthConsumption } = sessionsService
    const { useGetLastInvoice } = invoiceService
    const { useGetSelfEntity } = profileService
    const { data: monthSessions, sendRequest: fetchMonthConsumption, isLoading: monthLoading } = useGetMonthConsumption(new Date().toString())
    const { data: lastInvoice, sendRequest: fetchInvoice, isLoading: invoiceLoading } = useGetLastInvoice()
    const { data: profileEntity, sendRequest: fetchEntity, isLoading: entityLoading } = useGetSelfEntity()

    useEffect(() => {
        fetchMonthConsumption()
        fetchInvoice()
        fetchEntity()
    }, [])

    const getConsumption = () => {
        const sessions = monthSessions as SessionInfo[]
        return sessions.reduce((a, b) => a + (b.price || 0), 0);
    }

    const InvoiceStatus = (status: string) => {
        switch (status) {
            case "UNPAID":
                return <Error text={"Impayée"} />
            case "IN_PROGRESS":
                return <Waiting text={"En attente de paiement"} />
            case "PAID":
                return <Validation text={"Payée"} />
            default:
                return <Error text={"Impayée"} />
        }
    }

    return (
        <MainPageContentContainer sx={{ display: "flex", flexDirection: "column", gap: "1rem", height: "100%", alignItems: "center"}}>
            { !invoiceLoading ?
                <DashboardInvoiceCard
                    cardTitle={"Dernière facture"}
                    cardIcon={<img src={Money} alt='' />}
                    cardContent={
                        lastInvoice !== null ?
                            ( <>
                                    <Typography variant="titleLarge" sx={{position: "absolute", top: '3rem' }}> {(lastInvoice as InvoiceInfo).TotalWithTax} €</Typography>
                                    {InvoiceStatus((lastInvoice as InvoiceInfo).status.toString())}
                                </>
                            ) : (<></>)
                    }
                /> : <></>
            }{ !monthLoading ?
                <DashboardInvoiceCard
                    cardTitle={"Consommation en cours du mois"}
                    cardIcon={<img src={Thunder} alt=""/>}
                    cardContent={
                        monthSessions !== null ?
                            ( <>
                                    <Typography variant="titleLarge"> {getConsumption()} €</Typography>
                                </>
                            ) : (<></>)
                    }
                /> : <></>
            }
            <DashboardInvoiceCard
                cardTitle={"Moyen de paiement par défaut"}
                cardIcon={<EuroIcon sx={{color: "white"}} />}
                cardContent={
                    <Typography sx={{maxWidth: "150px"}}> Selon modalités
                        à réception de facture </Typography>
                }
            />
            { !entityLoading ?
                <DashboardInvoiceCard
                    cardTitle={"Adresse de facturation par défaut"}
                    cardIcon={<ReceiptIcon sx={{color: "white"}} />}
                    cardContent={
                        profileEntity !== null ?
                            ( <>
                                    <Typography> {(profileEntity as EntityInfo).address}</Typography>
                                    <Typography> {(profileEntity as EntityInfo).zip_code} - {(profileEntity as EntityInfo).city}</Typography>
                                </>
                            ) : (<></>)
                    }
                /> : <></>
            }
            <OreveButton value="Accéder aux finances" onClick={() => navigate(`/invoices`)}/>
        </MainPageContentContainer>
    )
}

interface DashboardInvoiceCardProps {
    cardTitle: string;
    cardIcon: ReactElement
    cardContent: ReactElement
}

const DashboardInvoiceCard: React.FC<DashboardInvoiceCardProps> = ({
    cardTitle, cardIcon, cardContent
}) => {
    return (
        <Card sx={{ padding: "12px", bgcolor: "grey.100", boxShadow: 0, borderRadius: '20px' }}>
            <Box sx={{ display: "flex", direction:'row', gap: "5rem", marginBottom: "1rem"}}>
                <Typography sx={{color: "grey.400", maxWidth: "150px", minWidth: "150px"}}>{cardTitle}</Typography>
                <Card sx={{display: "flex", bgcolor: "secondary.main", alignItems: "center", padding: "8px", borderRadius: "10px"}}>
                    {cardIcon}
                </Card>
            </Box>
            {cardContent}
        </Card>
    )
}